import React, { ReactElement } from "react";
import { Box, Link, Text, Flex, Checkbox } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";

import { isYoutubeEmbedUrl, isYoutubeNoCookieUrl } from "@/js/utils/youtubeUrlUtils";
import Tooltip from "rc-tooltip";
import { colours } from "@/utils/themeUtils";

export interface Item {
	text: string;
	value: string;
}

export enum ResourceType {
	Url,
	Signature,
}

/** This is not safe to be sent as a DTO as it does not match the API contract. */
export interface ResourceOption {
	label: string;
	/** Indicates whether the `resourceValue` property is a URL or signature. */
	resourceType: ResourceType;
	/** Actual URL or signature. Whether this is a URL or signature is indicated by the `resourceType` property. */
	resourceValue: string;
	/** Hidden flag controls displaying the resource in the UI. One example usage is Focus to YouTube video: a YouTube No Cookie entry is also added to the Focus alongside the focussed video (for Verdict compatibility), but the added entry is hidden to not confuse the teacher */
	hidden?: boolean;
}

export interface SelectedResource extends ResourceOption {
	openTab: boolean;
	/**
	 * True by defailt. Updated based on server-side validation outcome upon submission.
	 */
	isValid: boolean;

	isLockedFocusCompatible: boolean;

	/**
	 * The ID of the resource. This is only used when updating an existing focus resource.
	 */
	id?: string;
}

interface Props {
	selectedResources: SelectedResource[];
	onRemove: (resource: SelectedResource) => void;
	onCheckboxChanged: (resource: SelectedResource) => void;
	lockedFocus: boolean;
}

export function SelectedFocusResources(props: Props): ReactElement {
	const { t } = useTranslation();

	if (props.selectedResources.length === 0) {
		return <></>;
	}

	const anyResourcesAreInvalid = props.selectedResources.some((resource) => !resource.isValid);

	const anyFocusLockIncompatibleResources = props.selectedResources.some((resource) => !resource.isLockedFocusCompatible);

	const visibleResources = props.selectedResources.filter((resource) => !resource.hidden);

	return (
		<Box mt={3} data-testid="selected-focus-resources">
			{anyResourcesAreInvalid && (
				<Flex bg={colours.danger.background.weaker} p={3} rounded={"md"} my={3} data-testid="invalid-resource-selected">
					<Box mr={3} ml={1}>
						<ChakraFAIcon icon="fa-lock" variant="solid" color={colours.red[500]} width="0.8em" height="0.8em" />
					</Box>
					<Box flex={1}>
						<Text color={colours.red[500]} fontWeight={"medium"}>
							{t("focusLibrary.lockedRuleTitle")}
						</Text>
						<Text>
							{t("focusLibrary.lockedRulesNotAllowed")}{" "}
							<Link
								href="https://help.classwize.net/hc/en-gb/articles/6354876577948-Rules-Validation-Overview#h_01HBFRZWRGP0WXNKSP6DGVNDQ2"
								target="_blank"
							>
								{t("focusLibrary.lockedRulesLink")}
								<ChakraFAIcon icon="fa-arrow-up-right-from-square" width="0.8em" height="0.8em" ml={2} />
							</Link>
						</Text>
					</Box>
				</Flex>
			)}

			<Box border={props.lockedFocus && anyFocusLockIncompatibleResources ? "1px solid red" : "1px solid #eeeeee"} rounded={"md"} mt={3}>
				<Flex alignItems="center" px={5} py={2} fontWeight={500} shadow={"sm"}>
					<Box flex={1}>{t("focusLibrary.resourceColumn")}</Box>
					<Box mx={5}>
						<Flex alignItems="center" justifyContent="center" gap="8px">
							<Text lineHeight="16px" fontWeight="medium">
								{t("focusLibrary.openTab")}
							</Text>
							<Tooltip
								placement="topRight"
								trigger="hover"
								destroyTooltipOnHide={true}
								overlayClassName="Tooltip"
								overlayStyle={{ pointerEvents: "auto" }}
								overlay={
									<Text>
										{t("focusLibrary.openTabTooltip")}{" "}
										<a
											href="https://help.classwize.net/hc/en-gb/articles/11598018402332"
											target="_blank"
											rel="noopener noreferrer"
											style={{ cursor: "pointer", textDecoration: "underline" }}
										>
											{t("focusLibrary.findOutMore")}
										</a>
									</Text>
								}
								// The arrow will not be pointing to the icon if we don't offset it
								align={{ offset: [24, 0] }}
							>
								<ChakraFAIcon
									icon="fa-question-circle"
									variant="solid"
									color={colours.blueGray[400]}
									data-testid={"question"}
									width="0.8em"
									height="0.8em"
								/>
							</Tooltip>
						</Flex>
					</Box>
					<Box width={2}></Box>
				</Flex>
				{visibleResources.map((resource, index) => (
					<Flex
						key={index}
						style={{
							backgroundColor:
								!resource.isValid || (props.lockedFocus && !resource.isLockedFocusCompatible)
									? colours.danger.background.weaker
									: index % 2 === 0
										? "#F4F5F799"
										: "inherit",
						}}
						alignItems="center"
						px={5}
						py={3}
					>
						<Box flex={1}>
							<Text overflowWrap={"break-word"} wordBreak={"break-all"}>
								{resource.label}
							</Text>
						</Box>
						<Flex ml={5} alignItems="center">
							<Flex>
								<Checkbox
									name="alert"
									isChecked={resource.openTab}
									isDisabled={
										isYoutubeNoCookieUrl(resource.resourceValue) || isYoutubeEmbedUrl(resource.resourceValue) || props.lockedFocus
									}
									onChange={() => props.onCheckboxChanged(resource)}
									data-testid={`checkbox-${index}`}
								/>
								{props.lockedFocus && (
									<Box>
										<ChakraFAIcon
											ml={2}
											icon="fa-lock"
											variant="solid"
											color={colours.blueGray[400]}
											data-testid={"locked-focus-resource-lock-icon"}
											width="0.8em"
											height="0.8em"
										/>
									</Box>
								)}
							</Flex>
							<ChakraFAIcon
								ml={12}
								icon="fa-close"
								color={colours.danger.icon}
								cursor="pointer"
								onClick={() => props.onRemove(resource)}
								data-testid={`remove-${index}`}
							/>
						</Flex>
					</Flex>
				))}
			</Box>
			{props.lockedFocus && anyFocusLockIncompatibleResources && (
				<Box my={15} color={colours.red[500]} data-testid="invalid-resource-selected">
					<Text fontWeight={"medium"} fontSize={""}>
						{t("focusLibrary.cannotLockAllResources")}
					</Text>
					<Text fontSize={"sm"}>{t("focusLibrary.cannotLockAllResourcesDescription")}</Text>
				</Box>
			)}
		</Box>
	);
}
